import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import answersService from '../services/answersService';
import { 
  filterResponses, 
  mergeFieldsWithValues, 
  formatUserInfo, 
  prepareFormUpdateData,
  prepareFormDataForPDF 
} from '../utils/formDataUtils';
import { exportFormResponseToPDF } from '../../../utils/pdfExport';

/**
 * Form yanıtları için ana hook
 */
export const useAnswers = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState([]);
  const [folders, setFolders] = useState({});
  const [filteredResponses, setFilteredResponses] = useState([]);
  
  // Filters
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Redux selectors
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  /**
   * Klasör bilgilerini yükle
   */
  const loadFolders = useCallback(async () => {
    try {
      const folderMap = await answersService.loadFolders(appToken, userData.id);
      setFolders(folderMap);
      return folderMap;
    } catch (error) {
      console.error('Klasör yükleme hatası:', error);
      setFolders({});
      return {};
    }
  }, [appToken, userData.id]);

  /**
   * Form yanıtlarını yükle
   */
  const loadResponses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const responseData = await answersService.loadResponses(appToken, userData.id);
      
      console.log('Form yanıtları yüklendi:', responseData.length);
      console.log('Mevcut klasörler:', Object.keys(folders).length);
      
      const formsWithFolders = responseData.filter(r => r.parentId && r.parentId.trim() !== '');
      console.log('Klasör bilgisi olan formlar:', formsWithFolders.length);
      
      setResponses(responseData);
      return responseData;
    } catch (error) {
      console.error('Form yanıtları yükleme hatası:', error);
      setError(error.message || 'Form yanıtları yüklenirken bir hata oluştu.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [appToken, userData.id, folders]);

  /**
   * Tüm verileri baştan yükle
   */
  const initializeData = useCallback(async () => {
    try {
      await loadFolders();
      await loadResponses();
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      setError('Veriler yüklenirken bir hata oluştu.');
    }
  }, [loadFolders, loadResponses]);

  /**
   * Yanıtları filtrele
   */
  const applyFilters = useCallback(() => {
    const filtered = filterResponses(responses, {
      startDate,
      endDate,
      searchTerm,
      folders
    });
    setFilteredResponses(filtered);
  }, [responses, startDate, endDate, searchTerm, folders]);

  /**
   * Filtreleri temizle
   */
  const clearFilters = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
  }, []);

  // Effects
  useEffect(() => {
    initializeData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  return {
    // State
    loading,
    error,
    responses,
    folders,
    filteredResponses,
    
    // Filters
    startDate,
    endDate,
    searchTerm,
    setStartDate,
    setEndDate,
    setSearchTerm,
    
    // Actions
    loadFolders,
    loadResponses,
    initializeData,
    clearFilters,
    setError
  };
};

/**
 * Form düzenleme için hook
 */
export const useFormEdit = () => {
  const [editingResponse, setEditingResponse] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  /**
   * Form düzenleme detaylarını yükle
   */
  const handleEditResponse = async (response) => {
    try {
      const record = await answersService.getFormDetail(response.id, appToken, userData.id);
      
      if (record) {
        // API'den gelen verileri parse et
        let formContentObj = {};
        let formDataObj = {};

        try {
          formContentObj = typeof record.formContent === 'string' 
            ? JSON.parse(record.formContent)
            : record.formContent;
          
          formDataObj = typeof record.formData === 'string'
            ? JSON.parse(record.formData)
            : record.formData;
        } catch (error) {
          console.error('Veri parse edilirken hata:', error);
          formContentObj = {};
          formDataObj = {};
        }

        // Form alanlarını kullanıcı değerleriyle birleştir
        const fieldsWithValues = mergeFieldsWithValues(formContentObj, formDataObj);

        setEditingResponse({
          id: record.id,
          formId: formContentObj.id?.toString() || record.formId,
          formTitle: formContentObj.title || record.title,
          formDescription: formContentObj.description || '',
          formFields: fieldsWithValues,
          settings: {
            ...formContentObj.settings,
            documentControlEnabled: formContentObj.settings?.documentControlEnabled || false,
            documentPrefix: record.documentPrefix || formContentObj.settings?.documentPrefix || '',
            documentCode: record.documentCode || formContentObj.settings?.documentCode || '',
            department: record.department || formContentObj.settings?.department || ''
          },
          signature: formDataObj.signature || null,
          files: formDataObj.files || {}
        });

        setEditDialogOpen(true);
      }
    } catch (error) {
      console.error('Form düzenleme detayları yüklenirken hata:', error);
      throw new Error('Form düzenleme detayları yüklenirken bir hata oluştu.');
    }
  };

  /**
   * Form kaydet
   */
  const handleSaveForm = async (updatedFields, onSuccess) => {
    try {
      const hide = message.loading('Form kaydediliyor...', 0);
      console.log('Güncelleme başlıyor...');

      const userInfo = formatUserInfo(userData);
      const formData = prepareFormUpdateData(editingResponse, updatedFields, userInfo);
      
      console.log('Gönderilecek veri:', formData);

      await answersService.updateForm(formData, appToken);
      
      hide();
      setEditDialogOpen(false);
      message.success('Form başarıyla güncellendi');
      
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error('Form kaydetme hatası:', error);
      message.error(error.message);
    }
  };

  return {
    editingResponse,
    editDialogOpen,
    setEditingResponse,
    setEditDialogOpen,
    handleEditResponse,
    handleSaveForm
  };
};

/**
 * Form silme için hook
 */
export const useFormDelete = () => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [responseToDelete, setResponseToDelete] = useState(null);
  const [deleteExplanation, setDeleteExplanation] = useState('');
  const [deleteExplanationError, setDeleteExplanationError] = useState('');
  
  const appToken = useSelector((state) => state.appToken.apptoken);

  /**
   * Form sil
   */
  const handleDeleteResponse = async (onSuccess) => {
    const hide = message.loading('Form siliniyor...', 0);
    try {
      await answersService.deleteForm(
        responseToDelete.id, 
        deleteExplanation, 
        appToken
      );
      
      hide();
      setDeleteDialogOpen(false);
      setDeleteExplanation('');
      setDeleteExplanationError('');
      message.success('Form başarıyla silindi');
      
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      hide();
      console.error('Silme hatası:', error);
      message.error(error.message);
    }
  };

  /**
   * Silme dialogunu aç
   */
  const openDeleteDialog = (response) => {
    setResponseToDelete(response);
    setDeleteDialogOpen(true);
  };

  /**
   * Silme dialogunu kapat
   */
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setResponseToDelete(null);
    setDeleteExplanation('');
    setDeleteExplanationError('');
  };

  return {
    deleteDialogOpen,
    responseToDelete,
    deleteExplanation,
    deleteExplanationError,
    setDeleteExplanation,
    setDeleteExplanationError,
    handleDeleteResponse,
    openDeleteDialog,
    closeDeleteDialog
  };
};

/**
 * PDF export için hook
 */
export const usePDFExport = () => {
  const [loading, setLoading] = useState(false);
  
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  /**
   * PDF export
   */
  const handleExportToPDF = async (response) => {
    try {
      setLoading(true);
      const hide = message.loading('PDF oluşturuluyor...', 0);
      
      // Form detaylarını API'den al
      const record = await answersService.getFormDetail(response.id, appToken, userData.id);
      
      if (record) {
        const formDataForPDF = prepareFormDataForPDF(record);
        
        // PDF'i export et
        await exportFormResponseToPDF(formDataForPDF);
        hide();
        message.success('PDF başarıyla oluşturuldu');
      }
    } catch (error) {
      hide();
      console.error('PDF export hatası:', error);
      message.error('PDF oluşturulurken bir hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleExportToPDF
  };
};
